import { Link } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';

interface QuickLink {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
  permission?: string;
  feature?: string;
  teaser?: boolean;
  external?: boolean;
}

interface QuickLinksGridProps {
  title: string;
  links: QuickLink[];
  isLinkAccessible: (link: QuickLink) => boolean;
  isLinkVisible?: (link: QuickLink) => boolean;
}

export default function QuickLinksGrid({
  title,
  links,
  isLinkAccessible,
  isLinkVisible = () => true,
}: QuickLinksGridProps) {
  return (
    <div className="mb-12">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {links.filter(isLinkVisible).map((link) => {
          const accessible = isLinkAccessible(link);

          // Handle external links differently
          if (accessible && link.external) {
            return (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="p-3 rounded-xl transition-colors duration-200 bg-[#E34290]/10 text-[#E34290] group-hover:bg-[#E34290]/20">
                    <link.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-[#E34290] transition-colors duration-200">
                      {link.title}
                    </h3>
                  </div>
                  {link.teaser && (
                    <div className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full">
                      Coming Soon
                    </div>
                  )}
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{link.description}</p>
              </a>
            );
          }

          const LinkComponent = accessible ? Link : 'div';

          return (
            <LinkComponent
              key={link.href}
              to={accessible ? link.href : '/account?tab=subscription'}
              className={`
                group block p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 
                transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600
                ${accessible ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'}
              `}
            >
              <div className="flex items-center gap-4 mb-3">
                <div
                  className={`
                  p-3 rounded-xl transition-colors duration-200
                  ${
                    accessible
                      ? 'bg-[#E34290]/10 text-[#E34290] group-hover:bg-[#E34290]/20'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                  }
                `}
                >
                  <link.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-[#E34290] transition-colors duration-200">
                    {link.title}
                  </h3>
                </div>
                {link.teaser && (
                  <div className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full">
                    Coming Soon
                  </div>
                )}
                {!accessible && !link.teaser && (
                  <div className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-medium rounded-full">
                    Pro
                  </div>
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{link.description}</p>
            </LinkComponent>
          );
        })}
      </div>
    </div>
  );
}
